import { Link } from 'react-router-dom';
import { Award, CircleDashed, Globe, UserCheck, UserX, Users, XCircle } from 'lucide-react';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Select } from '../../components/form';
import { useDashboardFilters } from '../../components/DashboardFilters';
import { useCertificateResults, useFlows, useProjects } from '../../api/queries';
import type { CertTally } from '../../api/types';
import { formatPercent } from '../../lib/format';

/**
 * ნაკადის შედეგები — სერტიფიცირება მიმდინარე ეტაპზე.
 *
 * სამი სტატუსი რიგითია (არა → სერტ → სერტ + საერთაშორისო), ამიტომ ერთი ფერის
 * ღია→მუქი საფეხურებით ჩანს (brand ramp, OKLab L 0.87 → 0.75 → 0.46).
 * ფერი მარტო არასდროს ატარებს მნიშვნელობას: ლეგენდა + რიცხვები ცხრილში.
 */
const SEGMENTS = [
  { key: 'international', label: 'სერტ + საერთაშორისო', color: 'var(--color-brand-600)' },
  { key: 'cert', label: 'სერტ', color: 'var(--color-brand-300)' },
  { key: 'none', label: 'არა', color: 'var(--color-line-strong)' },
] as const;

const share = (n: number, total: number) =>
  total > 0 ? `${Math.round((n / total) * 100)}%` : '—';

export function FlowResultsPage() {
  const { filters, setProjectId, setFlowId } = useDashboardFilters();
  const { data, isLoading } = useCertificateResults(filters);
  const { data: projects } = useProjects();
  const { data: flows } = useFlows(filters.projectId || undefined);

  return (
    <>
      <PageHeader
        title="ნაკადის შედეგები"
        subtitle={`სერტიფიცირება მიმდინარე ეტაპზე · დასწრება მიმდინარე სტატუსით (ეტაპის ბოლოს საბოლოო), ზღვარი ${
          data?.minPct ?? 51
        }%`}
      />

      {/* --- ფილტრები: იგივე მდგომარეობა, რაც დეშბორდზე --- */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-[10px] border border-line bg-surface px-4 py-3 shadow-xs">
        <Select
          className="max-w-40"
          value={filters.projectId ?? ''}
          onChange={(e) => {
            setProjectId(e.target.value);
            setFlowId('');
          }}
          placeholder="ყველა პროექტი"
          options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
        />
        <Select
          className="max-w-44"
          value={filters.flowId ?? ''}
          onChange={(e) => setFlowId(e.target.value)}
          placeholder="ყველა ნაკადი"
          options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
        />
      </div>

      {isLoading || !data ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-6" />
        </div>
      ) : data.kpi.students === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState title="მონაცემი არ არის" description="არჩეულ ფილტრებში აქტიური სტუდენტი არ არის." />
        </div>
      ) : (
        <>
          {/* --- KPI: მოცულობა და დასწრება --- */}
          <div className="mb-4 grid gap-4 sm:grid-cols-3">
            <Kpi
              icon={<Users className="size-4" />}
              label="სტუდენტი"
              value={String(data.kpi.students)}
              meta="შეწყვეტილების ჩათვლით · გადაყვანილის გარეშე"
            />
            <Kpi
              icon={<UserCheck className="size-4" />}
              label="საშუალო დასწრება"
              value={formatPercent(data.kpi.avgCertPct, 1)}
              meta="მიმდინარე სტატუსით · შეწყვეტილების გარეშე"
            />
            <Kpi
              icon={<UserX className="size-4" />}
              label="შეწყვეტილი"
              value={String(data.kpi.dropped)}
              meta={`${share(data.kpi.dropped, data.kpi.students)} · სერტიფიკატი „არა", საშუალოში არ ითვლება`}
            />
          </div>

          {/* --- KPI: სერტიფიკატის სტატუსები --- */}
          <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi
              icon={<Globe className="size-4" />}
              label="სერტ + საერთაშორისო"
              value={String(data.kpi.international)}
              meta={`${share(data.kpi.international, data.kpi.students)} სტუდენტების`}
            />
            <Kpi
              icon={<Award className="size-4" />}
              label="სერტ"
              value={String(data.kpi.cert)}
              meta={`${share(data.kpi.cert, data.kpi.students)} სტუდენტების`}
            />
            <Kpi
              icon={<XCircle className="size-4" />}
              label="არა"
              value={String(data.kpi.none)}
              meta={`${share(data.kpi.none, data.kpi.students)} · დასწრება < ${data.minPct}% და არაფერი, ან შეწყვეტილი`}
            />
            <Kpi
              icon={<CircleDashed className="size-4" />}
              label="შეუფასებელი"
              value={String(data.kpi.notAssessed)}
              meta={`${share(data.kpi.notAssessed, data.kpi.students)} · მიმდინარე ეტაპზე შეფასება ჯერ არ არის`}
            />
          </div>

          {/* --- შესრულებული --- */}
          <Card title="შესრულებული" className="mb-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Cell label="მხოლოდ დავალება" value={data.kpi.taskOnly} total={data.kpi.students} />
              <Cell label="მხოლოდ პროექტი" value={data.kpi.projectOnly} total={data.kpi.students} />
              <Cell label="ორივე" value={data.kpi.both} total={data.kpi.students} />
              <Cell
                label="არაფერი"
                value={data.kpi.students - data.kpi.taskOnly - data.kpi.projectOnly - data.kpi.both}
                total={data.kpi.students}
              />
            </div>
          </Card>

          {/* --- ჯგუფები --- */}
          <Card title="ჯგუფების მიხედვით" action={<Legend />} className="mb-5">
            <div className="-mx-5 -my-5 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="border-b border-line bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                  <tr>
                    <th className="px-5 py-2.5 font-semibold">ჯგუფი</th>
                    <th className="px-4 py-2.5 font-semibold">კურსი</th>
                    <th className="px-4 py-2.5 font-semibold">ლექტორი</th>
                    <th className="px-4 py-2.5 text-right font-semibold">სტუდ.</th>
                    <th className="px-4 py-2.5 text-right font-semibold">დავ.</th>
                    <th className="px-4 py-2.5 text-right font-semibold">პროექ.</th>
                    <th className="px-4 py-2.5 text-right font-semibold">ორივე</th>
                    <th className="px-4 py-2.5 text-right font-semibold">დასწრება</th>
                    <th className="min-w-40 px-4 py-2.5 font-semibold">სერტიფიკატი</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byGroup.map((g) => (
                    <tr key={g.id} className="border-b border-line last:border-0 hover:bg-canvas/60">
                      <td className="px-5 py-3">
                        <Link
                          to={`/groups/${g.id}`}
                          className="tabular font-medium text-ink hover:text-brand-700 hover:underline"
                        >
                          {g.name}
                        </Link>
                        <span className="block text-[11px] text-ink-mute">
                          {g.flow} · ეტაპი {g.currentStage}
                          {g.totalLessons ? ` · ${g.totalLessons} ლექცია` : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{g.course}</td>
                      <td className="px-4 py-3 text-ink-soft">
                        {g.lecturers.map((l) => l.fullName).join(', ') || '—'}
                      </td>
                      <td className="tabular px-4 py-3 text-right text-ink">{g.students}</td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">
                        {g.taskOnly + g.both}
                      </td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">
                        {g.projectOnly + g.both}
                      </td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">{g.both}</td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">
                        {formatPercent(g.avgCertPct, 0)}
                      </td>
                      <td className="px-4 py-3">
                        <CertBar tally={g} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* --- კურსები --- */}
          <Card title="კურსების მიხედვით" action={<Legend />}>
            <div className="-mx-5 -my-5 overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="border-b border-line bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                  <tr>
                    <th className="px-5 py-2.5 font-semibold">კურსი</th>
                    <th className="px-4 py-2.5 text-right font-semibold">ჯგუფი</th>
                    <th className="px-4 py-2.5 text-right font-semibold">სტუდ.</th>
                    <th className="px-4 py-2.5 text-right font-semibold">დასწრება</th>
                    <th className="min-w-40 px-4 py-2.5 font-semibold">სერტიფიკატი</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byCourse.map((c) => (
                    <tr key={c.id} className="border-b border-line last:border-0 hover:bg-canvas/60">
                      <td className="px-5 py-3 font-medium text-ink">{c.name}</td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">{c.groups}</td>
                      <td className="tabular px-4 py-3 text-right text-ink">{c.students}</td>
                      <td className="tabular px-4 py-3 text-right text-ink-soft">
                        {formatPercent(c.avgCertPct, 0)}
                      </td>
                      <td className="px-4 py-3">
                        <CertBar tally={c} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}

// ============================================================

function Kpi({
  icon,
  label,
  value,
  meta,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  return (
    <div className="rounded-[10px] border border-line bg-surface p-4 shadow-xs">
      <div className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-ink-mute">
        {icon}
        {label}
      </div>
      <p className="tabular text-2xl font-semibold tracking-tight text-ink">{value}</p>
      <p className="mt-1 text-[11px] leading-relaxed text-ink-mute">{meta}</p>
    </div>
  );
}

function Cell({ label, value, total }: { label: string; value: number; total: number }) {
  return (
    <div className="rounded-lg bg-canvas px-3 py-2.5">
      <p className="text-[12px] text-ink-mute">{label}</p>
      <p className="tabular text-xl font-semibold text-ink">{value}</p>
      <p className="text-[11px] text-ink-mute">{share(value, total)}</p>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-soft">
      {SEGMENTS.map((s) => (
        <span key={s.key} className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm" style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
      {/* შეუფასებელი — ზოლის ცარიელი ნაწილი */}
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 rounded-sm border border-line-strong bg-canvas" />
        შეუფასებელი
      </span>
    </div>
  );
}

/**
 * განაწილების ზოლი: სეგმენტებს შორის 2px ზედაპირის ღრეჩო, კიდეები მომრგვალებული.
 * hover → რაოდენობა და წილი; რიცხვები ზოლის ქვეშაც წერია, ფერზე არ ვართ დამოკიდებული.
 */
function CertBar({ tally }: { tally: CertTally }) {
  const total = tally.students;
  return (
    <div>
      <div
        className="flex h-2 w-full gap-[2px] overflow-hidden rounded bg-canvas"
        title={tally.notAssessed ? `შეუფასებელი: ${tally.notAssessed} (${share(tally.notAssessed, total)})` : undefined}
      >
        {SEGMENTS.map((s) => {
          const n = tally[s.key];
          if (!n) return null;
          return (
            <span
              key={s.key}
              title={`${s.label}: ${n} (${share(n, total)})`}
              className="h-full first:rounded-l last:rounded-r"
              style={{ width: `${(n / total) * 100}%`, background: s.color }}
            />
          );
        })}
      </div>
      <p className="tabular mt-1 text-[11px] text-ink-mute">
        {tally.international} · {tally.cert} · {tally.none}
        {tally.notAssessed ? ` · ${tally.notAssessed} შეუფ.` : ''}
      </p>
    </div>
  );
}
